// No Firebase imports needed for stock data - using pure API calls
import { Stock } from '../types';
import { StockApiService } from './stockApiService';
import { twseApiService } from './TwseApiService';
import { twseRealtimeService } from './twseRealtimeService';
import { marketIndexService } from './marketIndexService';
import { logger } from '../utils/logger';
import { StockDataTransformer } from '../utils/stockDataTransformer';
import { StockNameManager } from './stockNameManager';
import { tpexApiService, TpexQuote } from './tpexApiService';
import { yahooFinanceService, YahooQuote } from './yahooFinanceService';
import staticStockDataService, { StaticStockInfo } from './staticStockDataService';

export class StockService {
  /**
   * 是否應該嘗試取得盤中/當日現價
   * 涵蓋交易時間 (09:00-13:30) 與官方收盤資料尚未更新的時段：
   * - 交易日 13:00 後整個晚上：實測官方收盤 OpenAPI 到深夜仍為前一日資料
   * - 週六 00:00-08:59：週五收盤可能尚未發布 (baseline 仍為週四收盤)
   * (週六 09:00 後、週日與交易日隔日 00:00-08:59 官方資料已更新，使用 baseline 即可)
   */
  private static shouldFetchRealtime(): boolean {
    return (
      marketIndexService.isMarketOpen() ||
      marketIndexService.isMarketRecentlyClosed() ||
      marketIndexService.isPostMarketSession()
    );
  }
  // Get all available stocks from TWSE OpenAPI
  static async getAllStocks(): Promise<Stock[]> {
    
    try {
      const stocks = await StockApiService.getTaiwanStocks();
      
      if (stocks.length > 0) {
        
        // 🔥 關鍵修復：將股票名稱更新到 StockNameManager
        const smartUpdates = stocks.map(stock => ({
          symbol: stock.symbol,
          sources: {
            apiName: stock.name,
            staticName: stock.name
          }
        }));
        
        StockNameManager.smartUpdateBatch(smartUpdates);
        
        return stocks;
      } else {
        return [];
      }
      
    } catch (error) {
      logger.stockService.error('Failed to get stocks from TWSE OpenAPI', error);
      return [];
    }
  }

  // Get stock by symbol from TWSE OpenAPI
  static async getStockBySymbol(symbol: string): Promise<Stock | null> {
    // 驗證股票代號格式，減少無效請求 (支援 2887I、00981A、00403A 等含字母後綴的代號)
    if (!symbol || typeof symbol !== 'string' || !/^\d{4,6}[A-Z]?$/i.test(symbol.trim())) {
      logger.stockService.debug('Invalid stock symbol format', { symbol });
      return null;
    }

    // 統一轉大寫，與靜態資料/快取的鍵一致 (避免 2887i 查不到 2887I)
    const cleanSymbol = symbol.trim().toUpperCase();
    let staticInfo: StaticStockInfo | null = null;
    try {
      staticInfo = await staticStockDataService.getStockInfo(cleanSymbol);
    } catch (error) {
      logger.stockService.debug('Unable to load static stock info for symbol', { symbol: cleanSymbol, error });
    }
    const isTpExSymbol = staticInfo?.market === '上櫃';
    const isEsbSymbol = staticInfo?.market === '興櫃';

    const updateNameCache = (resolved: Stock) => {
      if (resolved.name) {
        StockNameManager.smartUpdateStock(cleanSymbol, {
          apiName: resolved.name,
          staticName: resolved.name
        });
      }
    };

    if (!isTpExSymbol && !isEsbSymbol) {
      try {
        const stock = await StockApiService.getStockQuote(cleanSymbol);
        if (stock) {
          updateNameCache(stock);
          return stock;
        }
        logger.stockService.warn('TWSE OpenAPI failed for stock', { symbol });
      } catch (error) {
        logger.stockService.error('Failed to get stock by symbol', { symbol, error });
      }
    }

    if (isTpExSymbol || (!isTpExSymbol && !isEsbSymbol)) {
      try {
        const tpexQuote = await tpexApiService.getStockQuote(cleanSymbol);
        if (tpexQuote) {
          const tpexStock = this.convertTpexQuoteToStock(tpexQuote);
          updateNameCache(tpexStock);
          return tpexStock;
        }
      } catch (error) {
        logger.stockService.error('Failed to get TPEx mainboard stock by symbol', { symbol, error });
      }
    }

    if (isEsbSymbol || (!isTpExSymbol && !isEsbSymbol)) {
      try {
        const esbQuote = await tpexApiService.getEsbStockQuote(cleanSymbol);
        if (esbQuote) {
          const esbStock = this.convertTpexQuoteToStock(esbQuote);
          updateNameCache(esbStock);
          return esbStock;
        }
      } catch (error) {
        logger.stockService.error('Failed to get TPEx ESB stock by symbol', { symbol, error });
      }
    }

    return null;
  }

  /**
   * 取得即時股價 (使用 TWSE MIS API)
   * MIS 支援上市 (tse_) 與上櫃 (otc_) 前綴，興櫃股票僅能使用 TPEx ESB API
   * @param symbol 股票代號
   * @returns 即時股價資料
   */
  static async getRealtimeStockPrice(symbol: string): Promise<Stock | null> {
    try {
      logger.stockService.info('Fetching realtime stock price', { symbol });
      
      let staticInfo: StaticStockInfo | null = null;
      try {
        staticInfo = await staticStockDataService.getStockInfo(symbol);
      } catch (error) {
        logger.stockService.debug('Unable to load static stock info for realtime lookup', { symbol, error });
      }

      // 興櫃股票 MIS 不支援，先使用 ESB API (日統計性質，即時性受限)
      if (staticInfo?.market === '興櫃') {
        const esbQuote = await tpexApiService.getEsbStockQuote(symbol);
        if (esbQuote) {
          logger.stockService.info('Successfully fetched TPEx ESB realtime stock', { symbol, price: esbQuote.close });
          return this.convertTpexQuoteToStock(esbQuote);
        }
        // ESB 失敗時嘗試 Yahoo (可提供興櫃盤中價)
        const esbYahooQuote = await yahooFinanceService.getQuote(symbol);
        if (esbYahooQuote) {
          logger.stockService.info('Successfully fetched ESB stock via Yahoo fallback', { symbol, price: esbYahooQuote.currentPrice });
          return this.convertYahooQuoteToStock(esbYahooQuote);
        }
        return null;
      }

      // 上市與上櫃都優先使用 MIS 即時 API (determineExchange 自動處理 tse_/otc_ 前綴)
      const realtimeData = await twseRealtimeService.getStockPrice(symbol);
      
      if (realtimeData) {
        // 使用統一轉換工具
        const stock = StockDataTransformer.transformRealtimeStockPriceToStock(realtimeData);
        
        if (stock) {
          logger.stockService.info('Successfully fetched realtime stock', { symbol, price: stock.currentPrice });
          return stock;
        }
      }

      // MIS 失敗時，上櫃股票回退到 TPEx OpenAPI (收盤性質資料)
      if (staticInfo?.market === '上櫃') {
        const tpexQuote = await tpexApiService.getStockQuote(symbol);
        if (tpexQuote) {
          logger.stockService.info('Successfully fetched TPEx fallback stock', { symbol, price: tpexQuote.close });
          return this.convertTpexQuoteToStock(tpexQuote);
        }
      }

      // 最終備援：Yahoo Finance (當日最新價)
      const yahooQuote = await yahooFinanceService.getQuote(symbol);
      if (yahooQuote) {
        logger.stockService.info('Successfully fetched stock via Yahoo fallback', { symbol, price: yahooQuote.currentPrice });
        return this.convertYahooQuoteToStock(yahooQuote);
      }
      
      logger.stockService.warn('Failed to get realtime stock price', { symbol });
      return null;
    } catch (error) {
      logger.stockService.error('Error getting realtime stock price', { symbol, error });
      // 失敗時回退到一般 API
      return this.getStockBySymbol(symbol);
    }
  }

  /**
   * 批量取得即時股價
   * @param symbols 股票代號陣列
   * @returns 即時股價資料陣列
   */
  static async getRealtimeStockPrices(symbols: string[]): Promise<Stock[]> {
    if (symbols.length === 0) {
      return [];
    }

    try {
      logger.stockService.info('Fetching realtime stock prices', { symbolCount: symbols.length });
      const { twseSymbols, tpexSymbols, esbSymbols } = await this.categorizeSymbols(symbols);
      const results: Stock[] = [];
      const resolvedSymbols = new Set<string>();

      // 上市與上櫃都優先使用 MIS 即時 API (determineExchange 自動處理 tse_/otc_ 前綴)
      const misCandidates = [...twseSymbols, ...tpexSymbols];
      if (misCandidates.length > 0) {
        const realtimeDataList = await twseRealtimeService.getStockPrices(misCandidates);
        realtimeDataList.forEach(realtimeData => {
          const stock = StockDataTransformer.transformRealtimeStockPriceToStock(realtimeData);
          if (stock) {
            results.push(stock);
            resolvedSymbols.add(stock.symbol.toUpperCase());
          }
        });
      }

      // MIS 沒取得的上櫃股票，回退到 TPEx OpenAPI (收盤性質資料)
      // 個別 try/catch：單一來源失敗 (如 TPEx 封鎖代理 IP) 不得中斷其他來源與後續 Yahoo 備援
      if (tpexSymbols.length > 0) {
        try {
          const tpexQuotes = await tpexApiService.getQuotesBySymbols(tpexSymbols);
          tpexQuotes.forEach((quote, symbol) => {
            if (!resolvedSymbols.has(symbol.toUpperCase())) {
              results.push(this.convertTpexQuoteToStock(quote));
              resolvedSymbols.add(symbol.toUpperCase());
            }
          });
        } catch (error) {
          logger.stockService.warn('⚠️ TPEx 主板報價取得失敗，繼續處理其他來源', { error });
        }
      }

      if (esbSymbols.length > 0) {
        try {
          const esbQuotes = await tpexApiService.getEsbQuotesBySymbols(esbSymbols);
          esbQuotes.forEach((quote, symbol) => {
            results.push(this.convertTpexQuoteToStock(quote));
            resolvedSymbols.add(symbol.toUpperCase());
          });
        } catch (error) {
          logger.stockService.warn('⚠️ TPEx ESB 報價取得失敗，繼續處理其他來源', { error });
        }
      }

      // 官方來源 (MIS/TPEx/ESB) 都沒取得的代號 → Yahoo 最終備援
      // (如 TPEx OpenAPI 封鎖海外代理 IP 時，上櫃/興櫃股票全數落空)
      // 同樣以 try/catch 隔離：Yahoo 失敗不可丟棄已取得的 MIS/TPEx 結果
      const unresolved = symbols
        .map(symbol => symbol.toUpperCase())
        .filter(symbol => !resolvedSymbols.has(symbol));
      if (unresolved.length > 0) {
        try {
          const yahooQuotes = await yahooFinanceService.getQuotes(unresolved);
          const isRealtimeSession = this.shouldFetchRealtime();
          yahooQuotes.forEach((quote, symbol) => {
            const yahooStock = this.convertYahooQuoteToStock(quote);
            // 非交易時段取得的 Yahoo 價格實為收盤價，標記 close 以反映資料時點
            if (!isRealtimeSession) {
              yahooStock.priceAsOf = 'close';
            }
            results.push(yahooStock);
          });
          logger.stockService.info('🌐 Yahoo 批次備援完成', {
            candidates: unresolved.length,
            resolved: yahooQuotes.size,
          });
        } catch (error) {
          logger.stockService.warn('⚠️ Yahoo 批次備援失敗，保留已取得的官方來源報價', { error });
        }
      }

      logger.stockService.info('Successfully fetched realtime stocks', { fetchedCount: results.length });
      return results;
    } catch (error) {
      logger.stockService.error('Error getting realtime stock prices', { symbols, error });
      return [];
    }
  }

  /**
   * 智能取得股價 (優先使用即時API，失敗時回退到每日資料)
   * @param symbol 股票代號
   * @returns 股價資料
   */
  static async getSmartStockPrice(symbol: string): Promise<Stock | null> {
    // 在交易時段與收盤後晚間 (官方收盤資料未更新前)，優先使用即時 API
    if (this.shouldFetchRealtime()) {
      try {
        const realtimeStock = await this.getRealtimeStockPrice(symbol);
        if (realtimeStock) {
          return realtimeStock;
        }
      } catch (error) {
        logger.stockService.warn('Realtime API failed, falling back to daily data', { symbol, error });
      }
    }
    
    // 非交易時間或即時 API 失敗時，使用每日資料 (昨日收盤價)
    return this.getStockBySymbol(symbol);
  }

  /**
   * 優化的批量股票資料獲取
   * 統一處理股票名稱，優先使用有效名稱，避免顯示 (查無資料)
   * @param symbols 股票代號列表
   * @param existingStocks 現有股票資料
   * @returns 股票資料列表
   */
  static async getOptimizedStockData(symbols: string[], existingStocks?: Stock[]): Promise<Stock[]> {
    if (symbols.length === 0) {
      return [];
    }

    logger.stockService.info('🚀 開始股票資料獲取', {
      symbolCount: symbols.length,
      symbols: symbols.slice(0, 5)
    });
    const { normalizedSymbols, twseSymbols, tpexSymbols, esbSymbols, infoMap } = await this.categorizeSymbols(symbols);

    // 建立現有股票資料映射
    const existingStockMap = new Map<string, Stock>();
    if (existingStocks) {
      existingStocks.forEach(stock => {
        if (stock.symbol) {
          existingStockMap.set(stock.symbol.toUpperCase(), stock);
        }
      });
    }

    // 統一的股票名稱選擇函數
    const selectStockName = (symbol: string, apiName?: string, priceDataName?: string, existingName?: string): string => {
      // 記錄輸入的所有候選名稱
      logger.stockService.debug(`🔍 選擇股票名稱 ${symbol}`, {
        symbol,
        apiName,
        priceDataName,
        existingName,
        candidates: { apiName, priceDataName, existingName }
      });
      
      // 特殊處理：為缺少的常見股票提供預設名稱
      const knownStocks: Record<string, string> = {
        '2888': '新光金',
        // 可以根據需要添加更多常見股票
      };
      
      // 優先序列：API名稱 > 價格資料名稱 > 現有名稱 > 已知股票名稱 > 代號
      const candidates = [
        { source: 'API', value: apiName },
        { source: '價格資料', value: priceDataName },
        { source: '現有資料', value: existingName },
        { source: '預設名稱', value: knownStocks[symbol] }
      ];
      
      for (const candidate of candidates) {
        if (candidate.value && 
            candidate.value.trim() && 
            candidate.value !== symbol && 
            !candidate.value.includes('查無資料') &&
            candidate.value !== 'undefined' &&
            candidate.value !== 'null') {
          
          const selectedName = candidate.value.trim();
          
          // 特殊檢查：防止名稱重複連接
          if (selectedName.includes(symbol + symbol) || selectedName.length > 50) {
            logger.stockService.warn(`🚫 ${symbol} ${candidate.source}名稱疑似重複連接，跳過:`, {
              value: selectedName,
              length: selectedName.length
            });
            continue;
          }
          
          logger.stockService.info(`✅ ${symbol} 選用${candidate.source}名稱: "${selectedName}"`);
          return selectedName;
        } else {
          logger.stockService.debug(`❌ ${symbol} ${candidate.source}名稱無效:`, {
            value: candidate.value,
            isEmpty: !candidate.value || !candidate.value.trim(),
            isSymbol: candidate.value === symbol,
            hasNoDataText: candidate.value?.includes('查無資料'),
            isUndefined: candidate.value === 'undefined' || candidate.value === 'null'
          });
        }
      }
      
      logger.stockService.warn(`⚠️ ${symbol} 所有候選名稱都無效，使用代號`, {
        symbol,
        fallbackReason: '所有候選名稱都不符合有效條件'
      });
      return symbol; // 最後才使用代號
    };

    try {
      // 獲取股票名稱和即時價格
      const stockNamesMap = new Map<string, string>();
      normalizedSymbols.forEach(symbol => {
        const info = infoMap.get(symbol);
        if (info?.name) {
          stockNamesMap.set(symbol, info.name);
        }
      });

      // ===== Step 1: 昨日收盤價 baseline (優先取得，讓 UI 立即有價可顯示) =====
      const baselineMap = new Map<string, Stock>();

      if (twseSymbols.length > 0) {
        try {
          const dayData = await twseApiService.getAllStocksDayData();
          const twseSet = new Set(twseSymbols);
          dayData.forEach(item => {
            const symbol = item.stockCode?.trim().toUpperCase();
            if (symbol && twseSet.has(symbol)) {
              const stock = StockDataTransformer.transformTwseStockDataToStock(item);
              if (stock && stock.currentPrice > 0) {
                baselineMap.set(symbol, stock);
              }
            }
          });
          logger.stockService.info('📊 上市收盤 baseline 完成', { requested: twseSymbols.length, resolved: baselineMap.size });
        } catch (error) {
          logger.stockService.warn('⚠️ 取得上市收盤 baseline 失敗', { error });
        }
      }

      if (tpexSymbols.length > 0) {
        try {
          const tpexQuotes = await tpexApiService.getQuotesBySymbols(tpexSymbols);
          tpexQuotes.forEach((quote, symbol) => {
            baselineMap.set(symbol, this.convertTpexQuoteToStock(quote));
            stockNamesMap.set(symbol, quote.name);
          });
        } catch (error) {
          logger.stockService.warn('⚠️ 取得上櫃收盤 baseline 失敗', { error });
        }
      }

      if (esbSymbols.length > 0) {
        try {
          const esbQuotes = await tpexApiService.getEsbQuotesBySymbols(esbSymbols);
          esbQuotes.forEach((quote, symbol) => {
            baselineMap.set(symbol, this.convertTpexQuoteToStock(quote));
            stockNamesMap.set(symbol, quote.name);
          });
        } catch (error) {
          logger.stockService.warn('⚠️ 取得興櫃收盤 baseline 失敗', { error });
        }
      }

      // ===== Step 2: 盤中現價 (交易時段才嘗試，成功時覆蓋 baseline) =====
      // 上市 (tse_) 與上櫃 (otc_) 都走 MIS 即時 API；興櫃 MIS 不支援，維持 baseline
      // isRealtimeSession 只計算一次：跨 Step 2/2b 重複評估在時段交界 (如週六 09:00)
      // 會產生 Step 2 走即時、Step 2b 走 baseline-miss 的不一致分支
      const isRealtimeSession = this.shouldFetchRealtime();
      const realtimeMap = new Map<string, Stock>();
      const misCandidates = [...twseSymbols, ...tpexSymbols];
      if (isRealtimeSession && misCandidates.length > 0) {
        try {
          const [twseNamesMap, realtimePrices] = await Promise.all([
            twseRealtimeService.getBatchStockNames(misCandidates),
            twseRealtimeService.getStockPrices(misCandidates)
          ]);

          twseNamesMap.forEach((name, key) => stockNamesMap.set(key, name));
          realtimePrices.forEach(priceData => {
            const stock = StockDataTransformer.transformRealtimeStockPriceToStock(priceData);
            if (stock) {
              realtimeMap.set(stock.symbol.toUpperCase(), stock);
            }
          });
          logger.stockService.info('📈 盤中現價取得完成', { candidates: misCandidates.length, resolved: realtimeMap.size });
        } catch (error) {
          logger.stockService.warn('⚠️ 盤中現價取得失敗，保留收盤 baseline', { error });
        }
      }

      // ===== Step 2b: Yahoo Finance 備援 =====
      // 交易時段：
      // 1) MIS 沒取得的上市/上櫃股票 → Yahoo 當日最新價
      // 2) 興櫃股票 → Yahoo 盤中價 (MIS 不支援興櫃、ESB 僅日統計)
      // 非交易時段：為「官方 baseline 全失敗」的代號補 Yahoo 收盤價
      // (如 TPEx OpenAPI 封鎖海外代理 IP 時，上櫃/興櫃 baseline 取不到；
      //  Yahoo regularMarketPrice 收盤後即為當日收盤價，可作為 baseline 替代)
      // 「可用 baseline」= 有 entry 且價格 > 0：TPEx/ESB 停牌股的 close 可能為 0
      // ('--' 解析為 0)，此類代號 Step 3 會因 currentPrice <= 0 落入沿用現有資料，
      // 應視同 baseline 失敗交給 Yahoo 補價
      const hasUsableBaseline = (symbol: string) => {
        const baseline = baselineMap.get(symbol);
        return !!baseline && baseline.currentPrice > 0;
      };
      const yahooCandidates = isRealtimeSession
        ? [...misCandidates.filter(s => !realtimeMap.has(s.toUpperCase())), ...esbSymbols]
        : normalizedSymbols.filter(symbol => !hasUsableBaseline(symbol));

      if (yahooCandidates.length > 0) {
        try {
          const yahooQuotes = await yahooFinanceService.getQuotes(yahooCandidates);
          yahooQuotes.forEach((quote, symbol) => {
            const key = symbol.toUpperCase();
            const yahooStock = this.convertYahooQuoteToStock(quote);
            // 非交易時段取得的 Yahoo 價格實為收盤價，標記 close 以反映資料時點
            if (!isRealtimeSession) {
              yahooStock.priceAsOf = 'close';
            }
            const baselineStock = baselineMap.get(key);
            // Yahoo chart API 不提供成交量/OHLC，若 baseline (STOCK_DAY_ALL/TPEx) 有資料，
            // 保留其 volume/OHLC 等欄位，僅覆蓋價格相關欄位
            realtimeMap.set(
              key,
              baselineStock
                ? {
                    ...baselineStock,
                    currentPrice: yahooStock.currentPrice,
                    change: yahooStock.change,
                    changePercent: yahooStock.changePercent,
                    previousClose: yahooStock.previousClose,
                    lastUpdated: yahooStock.lastUpdated,
                    priceAsOf: isRealtimeSession ? 'realtime' : 'close',
                    dataSource: 'yahoo',
                  }
                : yahooStock
            );
          });
          logger.stockService.info('🌐 Yahoo 備援完成', {
            session: isRealtimeSession ? 'realtime' : 'baseline-miss',
            candidates: yahooCandidates.length,
            resolved: yahooQuotes.size,
          });
        } catch (error) {
          logger.stockService.warn('⚠️ Yahoo 備援失敗，沿用已取得的價格', { error });
        }
      }

      // ===== Step 3: 合併結果 (現價 > baseline > 現有資料) =====
      const results: Stock[] = [];

      normalizedSymbols.forEach(symbol => {
        const realtime = realtimeMap.get(symbol);
        const baseline = baselineMap.get(symbol);
        const existingStock = existingStockMap.get(symbol);

        const stockName = selectStockName(
          symbol,
          stockNamesMap.get(symbol),
          realtime?.name || baseline?.name,
          existingStock?.name
        );

        if (realtime) {
          // 盤中現價 (priceAsOf='realtime'，含 MIS 昨收 y 欄位)
          results.push({ ...realtime, name: stockName });
        } else if (baseline && baseline.currentPrice > 0) {
          // 昨日收盤 baseline (priceAsOf='close')
          results.push({ ...baseline, name: stockName });
        } else {
          // 全部來源失敗，沿用現有資料
          results.push({
            symbol,
            name: stockName,
            currentPrice: existingStock?.currentPrice || 0,
            change: existingStock?.change || 0,
            changePercent: existingStock?.changePercent || 0,
            volume: existingStock?.volume || 0,
            marketCap: existingStock?.marketCap || 0,
            lastUpdated: new Date(),
            previousClose: existingStock?.previousClose,
            priceAsOf: existingStock?.priceAsOf,
            dataSource: 'twse'
          });
        }
      });

      // 使用智能更新機制同時更新 StockNameManager
      const smartUpdates = results.map(stock => ({
        symbol: stock.symbol,
        sources: {
          realtimeName: stock.name,
          apiName: stockNamesMap.get(stock.symbol),
          staticName: existingStockMap.get(stock.symbol.toUpperCase())?.name
        }
      }));
      
      const updateResult = StockNameManager.smartUpdateBatch(smartUpdates);
      logger.stockService.info('📝 批量智能更新股票名稱完成', updateResult);
      
      logger.stockService.info('✅ 股票資料獲取完成', {
        requestedCount: normalizedSymbols.length,
        totalResults: results.length,
        validNames: results.filter(s => s.name !== s.symbol).length
      });
      
      return results;
      
    } catch (error) {
      logger.stockService.error('❌ 股票資料獲取失敗', error);
      return this.getRealtimeStockPrices(symbols);
    }
  }

  // Search stocks by name or symbol
  static async searchStocks(searchTerm: string): Promise<Stock[]> {
    try {
      // Note: Firestore doesn't support full-text search natively
      // This is a basic implementation - for production, consider using Algolia or similar
      const allStocks = await this.getAllStocks();
      
      const filteredStocks = allStocks.filter(stock => 
        stock.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        stock.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

      return filteredStocks;
    } catch (error) {
      logger.stockService.error('Failed to search stocks', error);
      return [];
    }
  }

  // Get top gainers
  static async getTopGainers(limit: number = 10): Promise<Stock[]> {
    try {
      const allStocks = await this.getAllStocks();
      return allStocks
        .filter(stock => stock.changePercent > 0)
        .sort((a, b) => b.changePercent - a.changePercent)
        .slice(0, limit);
    } catch (error) {
      logger.stockService.error('Failed to get top gainers', error);
      return [];
    }
  }

  // Get top losers
  static async getTopLosers(limit: number = 10): Promise<Stock[]> {
    try {
      const allStocks = await this.getAllStocks();
      return allStocks
        .filter(stock => stock.changePercent < 0)
        .sort((a, b) => a.changePercent - b.changePercent)
        .slice(0, limit);
    } catch (error) {
      logger.stockService.error('Failed to get top losers', error);
      return [];
    }
  }

  // Get stocks by market cap range
  static async getStocksByMarketCap(minMarketCap: number, maxMarketCap: number): Promise<Stock[]> {
    try {
      const allStocks = await this.getAllStocks();
      return allStocks.filter(stock => 
        stock.marketCap >= minMarketCap && stock.marketCap <= maxMarketCap
      );
    } catch (error) {
      logger.stockService.error('Failed to get stocks by market cap', error);
      return [];
    }
  }


  // Get market summary
  static async getMarketSummary() {
    try {
      const stocks = await this.getAllStocks();
      
      if (stocks.length === 0) {
        return null;
      }

      const topGainer = stocks.reduce((max, stock) => 
        stock.changePercent > max.changePercent ? stock : max
      );
      
      const topLoser = stocks.reduce((min, stock) => 
        stock.changePercent < min.changePercent ? stock : min
      );

      const totalMarketValue = stocks.reduce((sum, stock) => sum + stock.marketCap, 0);
      
      return {
        totalMarketValue,
        topGainer,
        topLoser,
        totalStocks: stocks.length,
        gainers: stocks.filter(s => s.changePercent > 0).length,
        losers: stocks.filter(s => s.changePercent < 0).length,
        unchanged: stocks.filter(s => s.changePercent === 0).length,
      };
    } catch (error) {
      logger.stockService.error('Failed to get market summary', error);
      return null;
    }
  }

  // Get stock price history from real API
  static async getStockHistory(symbol: string, period: '1D' | '5D' | '1M' | '3M' | '6M' | '1Y' = '1M') {
    try {
      logger.stockService.info('Fetching stock history', { symbol, period });
      
      // 首先嘗試從 TWSE API 獲取歷史資料
      try {
        const periodToDays = {
          '1D': 1,
          '5D': 5,
          '1M': 30,
          '3M': 90,
          '6M': 180,
          '1Y': 365
        };
        const days = periodToDays[period] || 30;
        const twseHistory = await this.getStockHistoryFromTwse(symbol, days);
        
        if (twseHistory && twseHistory.length > 0) {
          logger.stockService.info('Successfully fetched TWSE history records', { symbol, recordCount: twseHistory.length });
          return twseHistory;
        }
      } catch (twseError) {
        logger.stockService.warn('TWSE API failed, trying Yahoo Finance fallback', { symbol, error: twseError });
      }
      
      // Fallback to Yahoo Finance API
      const history = await StockApiService.getStockHistory(symbol, period);

      if (history && history.length > 0) {
        logger.stockService.info('Successfully fetched history records', { symbol, recordCount: history.length });
        return history;
      }

      // DATA-03:不再生成隨機模擬價格當真實資料回傳 —
      // 呼叫端無法區分真偽,可能據此做出錯誤投資判斷;無資料就回 null
      logger.stockService.warn('No history data available from all sources', { symbol, period });
      return null;
    } catch (error) {
      logger.stockService.error('Failed to get stock history', { symbol, error });
      return null;
    }
  }

  // ========== TWSE API Specific Methods ==========


  /**
   * Get stock by symbol from TWSE API (same as getStockBySymbol)
   */
  static async getStockBySymbolFromTwse(symbol: string): Promise<Stock | null> {
    // Just use the main getStockBySymbol method
    return await this.getStockBySymbol(symbol);
  }

  /**
   * Get stock historical data from TWSE API
   */
  static async getStockHistoryFromTwse(symbol: string, days: number = 30): Promise<any[]> {
    try {
      logger.stockService.info('Fetching TWSE stock history', { symbol });
      
      // 計算需要查詢的日期範圍
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - days);

      // DATA-03:TWSE OpenAPI 只提供「當日所有股票」資料,無法逐月取得歷史 —
      // 舊版在每月迴圈中重複抓同一份當日資料(1Y 期間同一筆重複 13 次)。
      // 改為只抓一次、依代號過濾,再以日期去重
      const historyData: Array<{
        date: string; openPrice: number; highPrice: number; lowPrice: number;
        closePrice: number; volume: number; change?: number; transaction?: number;
      }> = [];

      try {
        const allStocksData = await twseApiService.getAllStocksDayData();
        const seenDates = new Set<string>();
        for (const data of allStocksData) {
          if (data.stockCode !== symbol) continue;
          if (seenDates.has(data.date)) continue; // 去重
          seenDates.add(data.date);
          historyData.push(data);
        }
      } catch (fetchError) {
        logger.stockService.warn('Failed to fetch TWSE day data for history', { symbol, error: fetchError });
      }

      // 篩選出指定日期範圍內的資料
      const filteredData = historyData.filter(data => {
        const dataDate = new Date(data.date);
        return dataDate >= startDate && dataDate <= endDate;
      });
      
      // 轉換為統一格式
      return filteredData.map(data => ({
        date: data.date,
        open: data.openPrice,
        high: data.highPrice,
        low: data.lowPrice,
        close: data.closePrice,
        volume: data.volume,
        change: data.change,
        transaction: data.transaction
      }));
      
    } catch (error) {
      logger.stockService.error('Failed to get TWSE stock history', { symbol, error });
      return [];
    }
  }

  /**
   * Get market indices from TWSE API
   */
  static async getMarketIndicesFromTwse() {
    try {
      logger.stockService.info('Fetching market indices from TWSE');
      const indices = await twseApiService.getMarketIndices();
      
      return indices.map(index => ({
        name: index.indexName,
        value: index.closeIndex,
        change: this.parseChangeFromHtml(index.change),
        changePoints: index.changePoints,
        changePercent: index.changePercent,
        notes: index.notes
      }));
    } catch (error) {
      logger.stockService.error('Failed to fetch market indices from TWSE', error);
      return [];
    }
  }

  /**
   * Check TWSE API availability
   */
  static async isTwseApiAvailable(): Promise<boolean> {
    try {
      return await twseApiService.isApiAvailable();
    } catch (error) {
      logger.stockService.error('TWSE API availability check failed', error);
      return false;
    }
  }

  // ========== Helper Methods ==========



  /**
   * Estimate market cap (simple calculation)
   */
  private static estimateMarketCap(price: number, volume: number): number {
    // 這是一個簡化的市值估算，實際應該使用流通股數
    // 這裡使用成交量作為粗略估算
    return price * volume * 1000; // 假設流通股數約為成交量的1000倍
  }

  /**
   * Parse change indicator from HTML-formatted string
   */
  private static parseChangeFromHtml(htmlString: string): number {
    if (!htmlString) return 0;
    
    // 移除 HTML 標籤
    const cleanString = htmlString.replace(/<[^>]*>/g, '').trim();
    
    // 判斷正負
    if (cleanString.includes('+')) {
      return 1; // 上漲
    } else if (cleanString.includes('-')) {
      return -1; // 下跌
    }
    return 0; // 持平
  }

  private static convertTpexQuoteToStock(quote: TpexQuote): Stock {
    return {
      symbol: quote.symbol,
      name: quote.name,
      currentPrice: quote.close,
      change: quote.change,
      changePercent: quote.changePercent,
      volume: quote.volume,
      marketCap: 0,
      lastUpdated: quote.lastUpdated ?? new Date(),
      previousClose: Number((quote.close - quote.change).toFixed(2)), // TPEx 主板：昨收 = 收盤價 - 漲跌 (精確)；興櫃 ESB：ESB 無昨收欄位，此值為「前日均價」近似 (LatestPrice - PreviousAveragePrice 反推)
      priceAsOf: 'close',
      openPrice: quote.open,
      highPrice: quote.high,
      lowPrice: quote.low,
      lastBidPrice: quote.bidPrice,
      lastAskPrice: quote.askPrice,
      dataSource: quote.source === 'tpex-esb' ? 'tpex-esb' : 'tpex'
    };
  }

  /**
   * 將 Yahoo Finance 報價轉換為 Stock (當日最新價，作為 MIS 備援與興櫃盤中價來源)
   */
  private static convertYahooQuoteToStock(quote: YahooQuote): Stock {
    return {
      symbol: quote.symbol,
      name: quote.name || quote.symbol,
      currentPrice: quote.currentPrice,
      change: quote.change,
      changePercent: quote.changePercent,
      volume: 0, // chart API 不提供成交量
      marketCap: 0,
      lastUpdated: quote.marketTime ?? new Date(),
      previousClose: quote.previousClose,
      priceAsOf: 'realtime',
      dataSource: 'yahoo'
    };
  }

  private static async categorizeSymbols(symbols: string[]): Promise<{
    normalizedSymbols: string[];
    twseSymbols: string[];
    tpexSymbols: string[];
    esbSymbols: string[];
    infoMap: Map<string, StaticStockInfo | null>;
  }> {
    const normalizedSymbols = Array.from(new Set(symbols.map(symbol => symbol.toUpperCase())));
    const infoMap = new Map<string, StaticStockInfo | null>();

    await Promise.all(
      normalizedSymbols.map(async symbol => {
        try {
          const info = await staticStockDataService.getStockInfo(symbol);
          infoMap.set(symbol, info);
        } catch (error) {
          infoMap.set(symbol, null);
          logger.stockService.debug('Failed to load static stock info', { symbol, error });
        }
      })
    );

    const twseSymbols: string[] = [];
    const tpexSymbols: string[] = [];
    const esbSymbols: string[] = [];

    normalizedSymbols.forEach(symbol => {
      const market = infoMap.get(symbol)?.market;
      if (market === '上櫃') {
        tpexSymbols.push(symbol);
      } else if (market === '興櫃') {
        esbSymbols.push(symbol);
      } else {
        twseSymbols.push(symbol);
      }
    });
    return { normalizedSymbols, twseSymbols, tpexSymbols, esbSymbols, infoMap };
  }
}
